import { isNullOrWhitespace } from "../../../common/stringOperations.ts";
import { maxNotificationPeriodInDays, RelativeDateDirection, RelativeDateReference, RelativeDateUnit } from "../../../annualWheel/RelativeDate.types.ts";
import { AnnualWheelActivityGeneralType, COMPLIANCE_AREA_CONTRACT_MANAGEMENT_ID } from "../../../annualWheel/annualWheelDialog/AnnualWheelDialog.types.ts";
import { AnnualWheelActivitySubTask } from "../../../annualWheel/AnnualWheelSubTasks.types.ts";
import { RecurringIntervalType } from "../../../annualWheel/AnnualWheel.types.ts";

export interface EntityTemplateSelection {
    entityTemplateId: string;
    entityTemplateName: string;
    isMandatory: boolean;
}

export interface RelativeTemplateDateConfigModel {
    value: number | undefined;
    unit: RelativeDateUnit | undefined;
    direction: RelativeDateDirection | undefined;
    relativeTo: RelativeDateReference | undefined;
}

export class AnnualWheelTemplateModel {
    id: string = "";
    name: string;
    description: string | undefined;
    subTasks: Array<AnnualWheelActivitySubTask> = [];
    planNow: boolean = false;
    recurringInterval: number | undefined | null = undefined;
    recurringIntervalType: RecurringIntervalType | undefined;
    notificationPeriodInDays: number | undefined;
    complianceAreaIds: Array<string> = [];
    generalType: AnnualWheelActivityGeneralType | undefined;
    customId: string | undefined;
    startDateRelativeConfig?: RelativeTemplateDateConfigModel;
    endDateRelativeConfig?: RelativeTemplateDateConfigModel;
    entityTemplates: Array<EntityTemplateSelection> = [];
    businessAreaIds: Array<string> = [];
    tagIds: Array<string> = [];

    constructor(
        id: string = "",
        name: string = "",
        description?: string,
        subTasks: Array<AnnualWheelActivitySubTask> = [],
        planNow: boolean = false,
        recurringInterval?: number | null,
        notificationPeriodInDays?: number,
        complianceAreaIds: Array<string> = [],
        generalType: AnnualWheelActivityGeneralType | undefined = undefined,
        customId: string | undefined = undefined,
        recurringIntervalType?: RecurringIntervalType,
        startDateRelativeConfig?: RelativeTemplateDateConfigModel,
        endDateRelativeConfig?: RelativeTemplateDateConfigModel,
        entityTemplates: Array<EntityTemplateSelection> = [],
        businessAreaIds: Array<string> = [],
        tagIds: Array<string> = []
    ) {
        this.id = id;
        this.name = name;
        this.description = description;
        this.subTasks = subTasks;
        this.planNow = planNow;
        this.recurringInterval = recurringInterval;
        this.notificationPeriodInDays = notificationPeriodInDays;
        this.complianceAreaIds = complianceAreaIds;
        this.generalType = generalType;
        this.customId = customId;
        this.recurringIntervalType = recurringIntervalType;
        this.startDateRelativeConfig = startDateRelativeConfig;
        this.endDateRelativeConfig = endDateRelativeConfig;
        this.entityTemplates = entityTemplates;
        this.businessAreaIds = businessAreaIds;
        this.tagIds = tagIds;
    }

    static createDefaultTemplate(planNow?: boolean): AnnualWheelTemplateModel {
        return new AnnualWheelTemplateModel(
            "",
            "",
            undefined,
            [],
            planNow,
            12,
            14,
            [],
            undefined,
            undefined,
            RecurringIntervalType.Month,
            undefined,
            undefined
        );
    }
    static createDefaultObligationTemplate(): AnnualWheelTemplateModel {
        return new AnnualWheelTemplateModel(
            "",
            "",
            undefined,
            [],
            false,
            12,
            14,
            [COMPLIANCE_AREA_CONTRACT_MANAGEMENT_ID],
            AnnualWheelActivityGeneralType.Obligation,
            undefined,
            RecurringIntervalType.Month,
            undefined,
            undefined
        );
    }

    withUpdates(updates: Partial<AnnualWheelTemplateModel>): AnnualWheelTemplateModel {
        return new AnnualWheelTemplateModel(
            updates.id ?? this.id,
            updates.name ?? this.name,
            updates.description ?? this.description,
            updates.subTasks ?? this.subTasks,
            updates.planNow ?? this.planNow,
            "recurringInterval" in updates ? updates.recurringInterval : this.recurringInterval,
            "notificationPeriodInDays" in updates ? updates.notificationPeriodInDays : this.notificationPeriodInDays,
            updates.complianceAreaIds ?? this.complianceAreaIds,
            updates.generalType ?? this.generalType,
            updates.customId ?? this.customId,
            "recurringIntervalType" in updates ? updates.recurringIntervalType : this.recurringIntervalType,
            "startDateRelativeConfig" in updates ? updates.startDateRelativeConfig : this.startDateRelativeConfig,
            "endDateRelativeConfig" in updates ? updates.endDateRelativeConfig : this.endDateRelativeConfig,
            updates.entityTemplates ?? this.entityTemplates,
            updates.businessAreaIds ?? this.businessAreaIds,
            updates.tagIds ?? this.tagIds
        );
    }

    isInvalid = () => {
        return (
            this.isNameInvalid() ||
            this.isRecurringIntervalInvalid() ||
            this.isNotificationPeriodInvalid() ||
            this.isStartDateInvalid() ||
            this.isEndDateInvalid()
        );
    };

    isNameInvalid = () => {
        return isNullOrWhitespace(this.name);
    };

    isRecurringIntervalInvalid = () => {
        /* if interval is null when the activity should only appear once */
        if (this.recurringInterval === null) {
            return false;
        }

        return this.recurringInterval === undefined || this.recurringInterval < 1;
    };

    isNotificationPeriodInvalid = () => {
        return (
            this.notificationPeriodInDays === undefined ||
            this.notificationPeriodInDays === null ||
            this.notificationPeriodInDays < 1 ||
            this.notificationPeriodInDays > maxNotificationPeriodInDays
        );
    };

    convertToDays = (value: number, unit: RelativeDateUnit): number => {
        switch (unit) {
            case RelativeDateUnit.Days:
                return value;
            case RelativeDateUnit.Weeks:
                return value * 7;
            case RelativeDateUnit.Months:
                return value * 30;
            case RelativeDateUnit.Years:
                return value * 365;
            default:
                return value;
        }
    };

    hasAllDateConfigValues = (config?: RelativeTemplateDateConfigModel) => {
        return (
            config?.relativeTo !== undefined && config?.direction !== undefined && !isNullOrWhitespace(config?.value?.toString()) && config?.unit !== undefined
        );
    };

    hasAllDateConfigValuesUndefined = (config?: RelativeTemplateDateConfigModel) => {
        return (
            config?.relativeTo === undefined && config?.direction === undefined && isNullOrWhitespace(config?.value?.toString()) && config?.unit === undefined
        );
    };

    isStartDateInvalid = () => {
        const allValuesAreUndefined = this.hasAllDateConfigValuesUndefined(this.startDateRelativeConfig);
        const allValuesHasValues = this.hasAllDateConfigValues(this.startDateRelativeConfig);

        return this.startDateRelativeConfig !== undefined && !allValuesAreUndefined && !allValuesHasValues;
    };

    isEndDateInvalid = () => {
        const endDateAllValuesAreUndefined = this.hasAllDateConfigValuesUndefined(this.endDateRelativeConfig);
        const endDateAllValuesHasValues = this.hasAllDateConfigValues(this.endDateRelativeConfig);

        if (!endDateAllValuesAreUndefined && !endDateAllValuesHasValues) {
            return true;
        }

        const startDateHasValues = this.hasAllDateConfigValues(this.startDateRelativeConfig);

        if (startDateHasValues && endDateAllValuesHasValues) {
            const startRelativeTo = this.startDateRelativeConfig!.relativeTo!;
            const endRelativeTo = this.endDateRelativeConfig!.relativeTo!;

            if (startRelativeTo === endRelativeTo) {
                const startValue = this.startDateRelativeConfig!.value!;
                const endValue = this.endDateRelativeConfig!.value!;
                const startDirection = this.startDateRelativeConfig!.direction!;
                const endDirection = this.endDateRelativeConfig!.direction!;
                const startUnit = this.startDateRelativeConfig!.unit!;
                const endUnit = this.endDateRelativeConfig!.unit!;

                const startDaysOffset = this.convertToDays(startValue, startUnit) * (startDirection === RelativeDateDirection.After ? 1 : -1);
                const endDaysOffset = this.convertToDays(endValue, endUnit) * (endDirection === RelativeDateDirection.After ? 1 : -1);

                if (endDaysOffset < startDaysOffset) {
                    return true;
                }
            }
        }

        return false;
    };
}
